import React, { useState, useEffect, useCallback } from "react";
import { Receipt, TrendingUp, IndianRupee, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");

export default function AdminSales({ adminKey }) {
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({ daily: [], today_count: 0, today_value: 0, total_count: 0 });

  const load = useCallback(() => {
    const h = { "X-Admin-Key": adminKey };
    fetch(`${API}/sales`, { headers: h }).then((r) => r.json()).then((d) => setSales(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/sales/summary`, { headers: h }).then((r) => r.json()).then((d) => d.daily && setSummary(d)).catch(() => {});
  }, [adminKey]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (sales.length === 0) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Date & Time", "Item", "Code", "Brand", "Category", "Price", "Sold By"].join(",");
    const lines = sales.map((s) => [
      new Date(s.created_at).toLocaleString("en-IN"),
      s.name, s.code, s.brand, s.category, s.price, s.sold_by,
    ].map(esc).join(","));
    const blob = new Blob(["\ufeff" + [header, ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kapatia-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = [
    { label: "Sold Today", value: summary.today_count, icon: Receipt },
    { label: "Today's Value", value: inr(summary.today_value), icon: IndianRupee },
    { label: "Total Sales", value: summary.total_count, icon: TrendingUp },
  ];

  return (
    <div data-testid="admin-sales" className="grid gap-6">
      <div className="grid grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border bg-white px-5 py-5" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
            <s.icon size={18} style={{ color: "#E8501A" }} />
            <div className="mt-2 font-heading text-[26px] font-extrabold" style={{ color: "var(--green-900)" }}>{s.value}</div>
            <div className="mt-0.5 text-[12.5px] font-semibold uppercase tracking-wide" style={{ color: "var(--muted)" }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="rounded-2xl border bg-white" style={{ borderColor: "var(--line)" }}>
          <h3 className="border-b px-5 py-4 font-heading text-[15px] font-bold" style={{ borderColor: "var(--line)", color: "var(--green-950)" }}>Daily Summary (14 days)</h3>
          {summary.daily.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13.5px]" style={{ color: "var(--muted)" }}>No sales recorded yet.</p>
          ) : (
            <table className="w-full text-left text-[13.5px]">
              <thead>
                <tr className="font-heading text-[11.5px] uppercase tracking-wide" style={{ background: "var(--green-100)", color: "var(--green-950)" }}>
                  <th className="px-5 py-2.5">Date</th><th className="px-5 py-2.5">Items</th><th className="px-5 py-2.5">Value</th>
                </tr>
              </thead>
              <tbody>
                {summary.daily.map((d) => (
                  <tr key={d.date} className="border-t" style={{ borderColor: "var(--line)", color: "#31463a" }}>
                    <td className="px-5 py-2.5 font-semibold" style={{ color: "var(--green-950)" }}>
                      {new Date(d.date + "T00:00:00Z").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </td>
                    <td className="px-5 py-2.5">{d.count}</td>
                    <td className="px-5 py-2.5 font-semibold" style={{ color: "var(--orange-700)" }}>{inr(d.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-2xl border bg-white" style={{ borderColor: "var(--line)" }}>
          <div className="flex items-center justify-between border-b px-5 py-3.5" style={{ borderColor: "var(--line)" }}>
            <h3 className="font-heading text-[15px] font-bold" style={{ color: "var(--green-950)" }}>Recent Sales</h3>
            <button onClick={exportCsv} disabled={sales.length === 0} data-testid="sales-export-csv"
                    className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-heading text-[12px] font-bold disabled:opacity-50"
                    style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
              <Download size={13} style={{ color: "#E8501A" }} /> Export CSV
            </button>
          </div>
          {sales.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13.5px]" style={{ color: "var(--muted)" }}>Sales made from the warehouse Sale button will appear here.</p>
          ) : (
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full min-w-[560px] text-left text-[13.5px]">
                <thead>
                  <tr className="font-heading text-[11.5px] uppercase tracking-wide" style={{ background: "var(--green-100)", color: "var(--green-950)" }}>
                    <th className="px-4 py-2.5">When</th><th className="px-4 py-2.5">Item</th><th className="px-4 py-2.5">Code</th>
                    <th className="px-4 py-2.5">Price</th><th className="px-4 py-2.5">Sold By</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => (
                    <tr key={s.id} className="border-t" style={{ borderColor: "var(--line)", color: "#31463a" }}>
                      <td className="whitespace-nowrap px-4 py-2.5">{new Date(s.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                      <td className="max-w-[240px] px-4 py-2.5 font-semibold" style={{ color: "var(--green-950)" }}>{s.name}</td>
                      <td className="px-4 py-2.5 font-heading font-bold" style={{ color: "#E8501A" }}>{s.code || "-"}</td>
                      <td className="px-4 py-2.5">{inr(s.price)}</td>
                      <td className="px-4 py-2.5">
                        <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-800)" }}>{s.sold_by}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
